'use client';
import classNames from 'classnames';
import React from 'react';
import { SortFilterIcon } from './SortFilterIcon';

/**
 * `pinned` is a pill that sticks under whatever is pinned at the top. `floating`
 * is a round icon button fixed to the corner of the viewport below `md`, and the
 * same pinned pill from `md` up.
 */
export type TriggerPlacement = 'pinned' | 'floating';

const PILL =
  'inline-flex items-center gap-spacing/gap/x-xs rounded-full border border-borders/form-default bg-fill/card px-3 py-2 text-sm text-text/primary hover:bg-fill/alternative-1 md:text-base';

/**
 * Written mobile first: `max-*` variants are not available with this project's
 * `screens`. Each `md:` class puts back what the pill has, so a floating trigger
 * reads as the pill from `md` up.
 */
const FLOATING_BELOW_MD =
  'inline-flex items-center justify-center gap-spacing/gap/x-xs rounded-full text-sm ' +
  // Lifted by `--pinned-bottom-height` (whatever bar is pinned to the bottom) through a
  // `transform`, not `bottom`: the bar slides in and out as the page scrolls, and
  // every frame of an animated `bottom` is a layout shift counted into CLS.
  // `z-floating-trigger` keeps it above the bottom bar that slides in
  // under it (CMS-2106).
  'fixed bottom-6 right-4 z-floating-trigger size-14 translate-y-[calc(-1*var(--pinned-bottom-height,0px))] bg-buttons/primary p-0 text-text/on-dark shadow-md transition-[transform,background-color] duration-300 ease-out hover:bg-buttons/primary-hover ' +
  'md:static md:z-auto md:size-auto md:translate-y-0 md:justify-start md:border md:border-borders/form-default md:bg-fill/card md:px-3 md:py-2 md:text-base md:text-text/primary md:shadow-none md:transition-none md:hover:bg-fill/alternative-1';

/** Total over the placements, so a new one cannot compile without its classes. */
const CLASSES: Record<
  TriggerPlacement,
  { root: string; button: string; icon: string; label: string }
> = {
  pinned: {
    root: 'flex justify-end',
    button: PILL,
    icon: 'size-4 shrink-0',
    label: '',
  },
  floating: {
    // `contents` drops the row's box below md, so the fixed button leaves no gap.
    root: 'contents md:flex md:justify-end',
    button: FLOATING_BELOW_MD,
    icon: 'size-6 shrink-0 md:size-4',
    // Still the accessible name while the button is icon-only.
    label: 'sr-only md:not-sr-only',
  },
};

/**
 * The box around the trigger in the panel. Sticky so the trigger stays reachable
 * down the results (R2.2), below the travel trip bar or the loan bar where one is
 * pinned (`--pinned-top-height`). A floating trigger is fixed below md, so the box
 * must not exist there: a sticky, z-indexed one would be the stacking context its
 * z-index is confined to, and a table's sticky header would draw over it.
 */
const WRAPPER_CLASSES: Record<TriggerPlacement, string> = {
  pinned:
    'sticky top-[var(--pinned-top-height,0px)] z-sticky self-end lg:hidden',
  floating:
    'contents md:block md:sticky md:top-[var(--pinned-top-height,0px)] md:z-sticky md:self-end lg:hidden',
};

/**
 * From md up a floating trigger gives way to the button on the loan bar while the
 * page has one (`SortFilterBarButton`); below md it is still the fixed button.
 */
const FLOATING_BESIDE_BAR_BUTTON = 'contents md:hidden';

export const triggerWrapperClasses = (
  placement: TriggerPlacement,
  barButtonShown: boolean,
): string =>
  placement === 'floating' && barButtonShown
    ? FLOATING_BESIDE_BAR_BUTTON
    : WRAPPER_CLASSES[placement];

export type SortFilterTriggerProps = {
  label: string;
  /** Controls the drawer/sheet this opens, for assistive tech. */
  controls: string;
  expanded: boolean;
  onClick: () => void;
  /** Receives focus back when the drawer or sheet closes. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  placement?: TriggerPlacement;
};

/**
 * Opens the panel below the desktop breakpoint, where it is a drawer or a sheet
 * rather than a column. `rounded-full` stands in for the prototype's pill radius,
 * which has no token here.
 */
export const SortFilterTrigger: React.FC<SortFilterTriggerProps> = ({
  label,
  controls,
  expanded,
  onClick,
  buttonRef,
  placement = 'pinned',
}) => {
  const classes = CLASSES[placement];

  return (
    <div className={classes.root}>
      <button
        ref={buttonRef}
        type="button"
        data-component="sort-filter-trigger"
        onClick={onClick}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        // The overlay mounts only while open, so a closed id would dangle.
        aria-controls={expanded ? controls : undefined}
        className={classNames('mhg-alpha-typography-button-2', classes.button)}
      >
        <SortFilterIcon className={classes.icon} />
        <span className={classes.label || undefined}>{label}</span>
      </button>
    </div>
  );
};
